import React, { useEffect, useRef } from 'react';
import { Animated, StyleSheet, View } from 'react-native';
import type { AccessibilityRole } from 'react-native';
import { Text, useTheme } from 'react-native-paper';

export interface ChatBubbleProps {
  role: 'assistant' | 'user';
  children: React.ReactNode;
  /** Smaller secondary line inside the bubble (hints). */
  hint?: string;
  testID?: string;
  /**
   * Exposes live-region and heading semantics for screen readers.
   * Use on the active assistant question bubble so AT announces it when
   * it appears (Android/web via live region; iOS via a separate
   * AccessibilityInfo call in the parent).
   */
  accessibilityLiveRegion?: 'none' | 'polite' | 'assertive';
  accessibilityRole?: AccessibilityRole;
}

/**
 * Conversational registration (M13): a single chat bubble with a spring
 * entrance (fade + slide up). Assistant speaks on the left, the user's
 * answers land on the right — the register funnel reads as a conversation,
 * not a form.
 */
export const ChatBubble: React.FC<ChatBubbleProps> = ({
  role,
  children,
  hint,
  testID,
  accessibilityLiveRegion,
  accessibilityRole,
}) => {
  const theme = useTheme();
  const opacity = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(14)).current;

  useEffect(() => {
    Animated.parallel([
      Animated.timing(opacity, { toValue: 1, duration: 220, useNativeDriver: true }),
      Animated.spring(translateY, { toValue: 0, friction: 7, tension: 60, useNativeDriver: true }),
    ]).start();
  }, [opacity, translateY]);

  const isUser = role === 'user';

  // A bubble built from several text pieces (`{label}{' — '}{price}`) used to
  // fall through to the raw branch: React Native Web then rendered bare text
  // nodes inside the View, losing the theme colour, the Paper typography and
  // the wrapping that keeps the bubble inside its 85% max width. Collapse any
  // all-text children into the same single Text as the string case; element
  // children (the pricing review block) still render as-is.
  const textChildren = React.Children.toArray(children);
  const isTextOnly =
    textChildren.length > 0 &&
    textChildren.every(child => typeof child === 'string' || typeof child === 'number');

  return (
    <Animated.View
      style={[
        styles.row,
        isUser ? styles.rowUser : styles.rowAssistant,
        { opacity, transform: [{ translateY }] },
      ]}
      testID={testID}
      accessibilityLiveRegion={accessibilityLiveRegion}
      accessibilityRole={accessibilityRole}
    >
      <View
        style={[
          styles.bubble,
          isUser
            ? [styles.bubbleUser, { backgroundColor: theme.colors.primary }]
            : [styles.bubbleAssistant, { backgroundColor: theme.colors.surfaceVariant }],
        ]}
      >
        {isTextOnly ? (
          <Text
            variant="bodyLarge"
            style={{ color: isUser ? theme.colors.onPrimary : theme.colors.onSurfaceVariant }}
          >
            {textChildren.join('')}
          </Text>
        ) : (
          children
        )}
        {hint ? (
          <Text
            variant="bodySmall"
            style={[styles.hint, { color: isUser ? theme.colors.onPrimary : theme.colors.onSurfaceVariant }]}
          >
            {hint}
          </Text>
        ) : null}
      </View>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  row: {
    width: '100%',
    marginVertical: 4,
    flexDirection: 'row',
  },
  rowAssistant: { justifyContent: 'flex-start' },
  rowUser: { justifyContent: 'flex-end' },
  bubble: {
    maxWidth: '85%',
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 18,
  },
  bubbleAssistant: { borderTopLeftRadius: 6 },
  bubbleUser: { borderBottomRightRadius: 6 },
  hint: {
    marginTop: 4,
    opacity: 0.8,
  },
});

export default ChatBubble;
